import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LANGS, MESSAGES, type Lang } from './locales';

/**
 * Jazyk výlohy (Fáze 2). Volba se pamatuje v prohlížeči a promítá se do
 * `<html lang>`. `t(key, vars)` vrací text z aktivního slovníku s fallbackem
 * na češtinu a nakonec na samotný klíč (aby chybějící překlad nespadl).
 */
const STORAGE_KEY = 'zooshopik.lang';

interface LangContextValue {
  readonly lang: Lang;
  setLang(lang: Lang): void;
  t(key: string, vars?: Record<string, string | number>): string;
}

const LangContext = createContext<LangContextValue | null>(null);

function initialLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return (LANGS as readonly string[]).includes(stored ?? '') ? (stored as Lang) : 'cs';
  } catch {
    return 'cs';
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  // Drž `<html lang>` v souladu s aktivním jazykem i po prvním vykreslení.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Privátní režim – volba prostě nepřežije zavření okna.
    }
    if (typeof document !== 'undefined') document.documentElement.lang = next;
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const dict = MESSAGES[lang] ?? MESSAGES.cs;
      let text = dict[key] ?? MESSAGES.cs[key] ?? key;
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replace(new RegExp(`\\{${name}\\}`, 'g'), String(value));
        }
      }
      return text;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT(): LangContextValue {
  const context = useContext(LangContext);
  if (context === null) throw new Error('useT musí být uvnitř <LanguageProvider>.');
  return context;
}
